export interface CopyTextResult {
  ok: boolean;
  reason?: 'permission-denied';
}

async function clipboardWritePermissionState(): Promise<PermissionState | null> {
  if (!window.isSecureContext || !navigator.clipboard?.writeText) return null;
  if (!navigator.permissions?.query) return 'prompt';

  return navigator.permissions
    .query({ name: 'clipboard-write' as PermissionName })
    .then((result) => result.state)
    .catch((): PermissionState => 'prompt');
}

function copyBySelection(value: string): boolean {
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', 'true');
  textarea.style.position = 'fixed';
  textarea.style.top = '0';
  textarea.style.left = '0';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.focus({ preventScroll: true });
  textarea.select();
  textarea.setSelectionRange(0, value.length);
  const copied = document.execCommand('copy');
  textarea.remove();
  return copied;
}

/** 复制文本到剪贴板，兼容 Clipboard API 不可用的场景。 */
export async function copyText(value: string): Promise<CopyTextResult> {
  const permissionState = await clipboardWritePermissionState();
  const denied = permissionState === 'denied';

  let permissionError = denied;

  if (permissionState === 'granted' || permissionState === 'prompt') {
    const copied = await navigator.clipboard.writeText(value).then(
      () => true,
      (error: unknown) => {
        permissionError =
          error instanceof DOMException &&
          (error.name === 'NotAllowedError' || error.name === 'SecurityError');
        return false;
      },
    );
    if (copied) return { ok: true };
  }

  if (copyBySelection(value)) return { ok: true };
  return permissionError ? { ok: false, reason: 'permission-denied' } : { ok: false };
}
